import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import moment from 'moment';
import { ReloadOutlined, ClockCircleOutlined } from '@ant-design/icons';
import ExpansionPanel from 'zignal-landscape/lib/ExpansionPanel';
import ExpansionPanelBody from 'zignal-landscape/lib/ExpansionPanelBody';
import ExpansionPanelTitle from 'zignal-landscape/lib/ExpansionPanelTitle';
import ThemeProvider from 'zignal-landscape/lib/ThemeProvider';
import ToolTip from 'zignal-landscape/lib/ToolTip';
import { Light } from 'themes';
import {
  fetchSummaryStart,
  getCacheInfo,
  getDashboardIntention,
  getDashboardIntentionPreset,
  getError,
  getFeatureEnabled,
  getGeneratedAt,
  getHasContent,
  getIsEditingIntention,
  getIsExpanded,
  getIsLoading,
  getJsonContent,
  getShowIntentionFlow,
  hideIntentionFlow,
  setDashboardIntention,
  setDashboardIntentionPreset,
  setFeatureEnabled,
  toggleSummaryPanel
} from './DashboardSummary.ducks';
import IntentionFlow from './IntentionFlow';
import EventCollapse from './EventCollapse';
import styles from './DashboardSummary.css';

const isValidScore = score => {
  if (
    score === undefined ||
    score === null ||
    score === 'NA' ||
    score === 'N/A'
  ) {
    return false;
  }
  const num = typeof score === 'string' ? parseFloat(score) : score;
  return !isNaN(num);
};

const getRelevanceClass = (score, sx) => {
  if (
    score === undefined ||
    score === null ||
    score === 'NA' ||
    score === 'N/A' ||
    (typeof score === 'string' && isNaN(parseFloat(score)))
  ) {
    return '';
  }

  const numScore = typeof score === 'string' ? parseFloat(score) : score;

  if (isNaN(numScore)) {
    return '';
  }

  if (numScore >= 0.7) {
    return sx.highRelevance;
  }
  if (numScore >= 0.4) {
    return sx.mediumRelevance;
  }
  return sx.lowRelevance;
};

const formatDateTime = dateString => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const DashboardSummary = ({
  dashboardId,
  account = null,
  activeFilter,
  activeProfileId = null,
  // Redux props
  dispatch,
  isLoading,
  jsonContent = null,
  isExpanded,
  hasContent,
  featureEnabled,
  error = null,
  showIntentionFlow,
  dashboardIntention = null,
  isEditingIntention,
  cacheInfo = null,
  generatedAt = null,
  dashboardIntentionPreset = null
}) => {
  const [panelsOpen, setPanelsOpen] = useState({});

  // Get dashboard widgets from window.Dashboard (Backbone model)
  const dashboardWidgets = React.useMemo(() => {
    try {
      if (window.Dashboard && typeof window.Dashboard.get === 'function') {
        const widgets = window.Dashboard.get('widgets');
        if (widgets && typeof widgets.toJSON === 'function') {
          // Backbone collection - convert to array
          return widgets.toJSON();
        } else if (Array.isArray(widgets)) {
          return widgets;
        }
      }
      return [];
    } catch (error) {
      console.error('Error accessing dashboard widgets:', error);
      return [];
    }
  }, [dashboardId]);
  const [refreshingCluster, setRefreshingCluster] = useState(null);
  const [showAllClusters, setShowAllClusters] = useState(false);
  const summaryPanelRef = useRef(null);
  const dashboardElementRef = useRef(null);
  const originalMarginTopRef = useRef(null);

  useEffect(() => {
    const dashboardElement = document.getElementById('realtime-dashboard');
    if (dashboardElement) {
      dashboardElementRef.current = dashboardElement;
      const computedStyle = window.getComputedStyle(dashboardElement);
      originalMarginTopRef.current = computedStyle.marginTop;
    }
  }, []);

  useEffect(() => {
    const enabled = account && account.summaryFeatureEnabled === true;
    dispatch(setFeatureEnabled(enabled));
  }, [account, dashboardId, dispatch]);

  useEffect(() => {
    if (!dashboardElementRef.current) {
      return;
    }

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    const transitionStyle = prefersReducedMotion
      ? 'none'
      : 'margin-top 2s cubic-bezier(0.4, 0, 0.2, 1)';

    if (isExpanded && summaryPanelRef.current) {
      // Calculate actual height
      const summaryPanel = summaryPanelRef.current;
      const originalMaxHeight = summaryPanel.style.maxHeight;
      const originalTransition = summaryPanel.style.transition;

      summaryPanel.style.transition = 'none';
      summaryPanel.style.maxHeight = 'none';
      const actualHeight = summaryPanel.scrollHeight;

      summaryPanel.style.maxHeight = originalMaxHeight;
      summaryPanel.style.transition = originalTransition;

      setTimeout(
        () => {
          const originalMarginValue =
            parseInt(originalMarginTopRef.current || '0', 10) || 0;
          const newMarginTop = originalMarginValue + actualHeight;

          dashboardElementRef.current.style.transition = transitionStyle;
          dashboardElementRef.current.style.marginTop = `${newMarginTop}px`;
        },
        prefersReducedMotion ? 0 : 50
      );
    } else {
      dashboardElementRef.current.style.transition = transitionStyle;
      dashboardElementRef.current.style.marginTop =
        originalMarginTopRef.current || '';
    }
  }, [isExpanded, isLoading, hasContent, jsonContent]);

  useEffect(() => {
    return () => {
      if (dashboardElementRef.current && isExpanded) {
        dashboardElementRef.current.style.marginTop =
          originalMarginTopRef.current || '';
        dashboardElementRef.current.style.transition = '';
      }
    };
  }, [isExpanded]);

  const handleToggle = useCallback(() => {
    dispatch(toggleSummaryPanel());
  }, [dispatch]);

  const handleIntentionSave = useCallback(
    async (intention, presetId = null) => {
      try {
        if (dashboardId) {
          const response = await fetch(
            `/api/ai/dashboards/${dashboardId}/intention`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              credentials: 'include',
              body: JSON.stringify({
                intention,
                intentionPreset: presetId, // Save the preset ID
                llmApproved: true,
                profileIds: activeProfileId ? [activeProfileId] : []
              })
            }
          );

          if (!response.ok) {
            const errorData = await response.json();
            console.error('API error response:', errorData);
            throw new Error(errorData.error || 'Failed to save intention');
          }
        } else {
          console.warn('No dashboardId provided, skipping API save');
        }

        // Save both intention text and preset ID to Redux
        dispatch(setDashboardIntention(intention));
        if (presetId) {
          dispatch(setDashboardIntentionPreset(presetId));
        }

        if (dashboardId) {
          dispatch(
            fetchSummaryStart({ dashboardId, activeFilter, activeProfileId })
          );
        }
      } catch (err) {
        console.error('Failed to save dashboard intention:', err);
        dispatch(setDashboardIntention(intention));
        if (presetId) {
          dispatch(setDashboardIntentionPreset(presetId));
        }
      }
    },
    [dispatch, dashboardId, activeFilter, activeProfileId]
  );

  const handleIntentionCancel = useCallback(() => {
    dispatch(hideIntentionFlow());

    if (!isEditingIntention && !dashboardIntention) {
      dispatch(toggleSummaryPanel());
    }
  }, [dispatch, isEditingIntention, dashboardIntention]);

  // Load existing intention and preset when dashboard mounts
  useEffect(() => {
    const loadIntention = async () => {
      if (dashboardId && !dashboardIntention) {
        try {
          const response = await fetch(
            `/api/ai/dashboards/${dashboardId}/intention`,
            {
              method: 'GET',
              credentials: 'include'
            }
          );

          if (response.ok) {
            const data = await response.json();
            dispatch(setDashboardIntention(data.intention)); // Will be null if no intention is saved
            if (data.intentionPreset) {
              dispatch(setDashboardIntentionPreset(data.intentionPreset));
            }
          } else if (response.status !== 404) {
            // Only log error if it's not a 404 (which is expected when no intention exists)
            throw new Error(`Failed to load intention: ${response.statusText}`);
          }
        } catch (err) {
          console.error('Failed to load dashboard intention:', err);
        }
      }
    };

    loadIntention();
  }, [dashboardId, dashboardIntention, dispatch]);

  // Reset summary when active profile changes
  useEffect(() => {
    if (hasContent && dashboardId && activeProfileId) {
      // Clear existing content and re-fetch with new profile
      dispatch(
        fetchSummaryStart({ dashboardId, activeFilter, activeProfileId })
      );
    }
  }, [activeProfileId]); // Only depend on activeProfileId to avoid infinite loops

  const getStatusIndicator = () => {
    if (isLoading) {
      return (
        <span className={classnames(styles.statusIndicator, styles.loading)} />
      );
    }
    if (error) {
      return (
        <span className={classnames(styles.statusIndicator, styles.error)} />
      );
    }
    if (hasContent) {
      return (
        <span className={classnames(styles.statusIndicator, styles.success)} />
      );
    }
    return null;
  };

  const renderEventContent = event => {
    return (
      <div className={styles.eventContent}>
        <div className={styles.eventMeta}>
          <span className={styles.peakTime}>
            {formatDateTime(event['peak-time'])}
          </span>
        </div>
        <p className={styles.eventSummary}>{event.summary}</p>
      </div>
    );
  };

  // Handler for toggling individual expansion panels (accordion behavior - only one open at a time)
  const handlePanelToggle = useCallback((index) => {
    setPanelsOpen(prev => {
      const isCurrentlyOpen = prev[index];
      // If clicking an open panel, close it. Otherwise, close all and open the clicked one
      if (isCurrentlyOpen) {
        return { [index]: false };
      }
      return { [index]: true };
    });
  }, []);

  const handleRefreshSummary = useCallback(() => {
    if (dashboardId) {
      dispatch(
        fetchSummaryStart({
          dashboardId,
          activeFilter,
          activeProfileId,
          forceRefresh: true
        })
      );
    }
  }, [dashboardId, activeFilter, activeProfileId, dispatch]);

  const getCacheStatusDisplay = useCallback(() => {
    if (!cacheInfo || !generatedAt) {
      return null;
    }

    const { fromCache } = cacheInfo;
    const age = moment().diff(moment(generatedAt), 'minutes');

    if (fromCache) {
      if (age < 60) {
        return `Generated ${age} ${age === 1 ? 'minute' : 'minutes'} ago`;
      }
      const hours = Math.floor(age / 60);
      return `Generated ${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    }

    return 'Fresh data';
  }, [cacheInfo, generatedAt]);

  // The following workaround is due to height sizing issues with the sub-collapses
  // Function to force parent cluster to recalculate height
  const forceParentRecalculation = useCallback(() => {
    // This is handled by ExpansionPanel internally now
  }, []);

  const renderClusterContent = cluster => {
    const events = cluster.events || [];

    return (
      <div
        className={classnames(styles.clusterContent, {
          [styles.refreshing]:
            refreshingCluster === `cluster-${cluster.cluster_id}`
        })}
        style={{
          overflow: 'visible',
          height: 'auto',
          maxHeight: 'none'
        }}
      >
        <div className={styles.clusterMeta}>
          <span className={styles.clusterSize}>
            Size: {cluster.cluster_size}
          </span>
        </div>
        <p className={styles.clusterSummary}>{cluster.cluster_summary}</p>

        <EventCollapse
          events={events}
          cluster={cluster}
          renderEventContent={renderEventContent}
          onEventToggle={forceParentRecalculation}
        />
      </div>
    );
  };

  let parsedContent = null;

  const renderClustersSection = () => {
    // Check if any cluster has valid numeric relevance scores
    const hasRelevanceScores = parsedContent.events.some(c =>
      isValidScore(c.relevance_score)
    );

    // Only apply relevance filtering if valid scores exist
    const relevantClusters = hasRelevanceScores
      ? parsedContent.events.filter(c => {
        if (!isValidScore(c.relevance_score)) {
          return true;
        } // Show items with NA/invalid scores
        const score =
            typeof c.relevance_score === 'string'
              ? parseFloat(c.relevance_score)
              : c.relevance_score;
        return score > 0.3;
      })
      : parsedContent.events;

    const irrelevantClusters = hasRelevanceScores
      ? parsedContent.events.filter(c => {
        if (!isValidScore(c.relevance_score)) {
          return false;
        }
        const score =
            typeof c.relevance_score === 'string'
              ? parseFloat(c.relevance_score)
              : c.relevance_score;
        return score <= 0.3;
      })
      : [];

    const displayClusters = showAllClusters
      ? parsedContent.events
      : relevantClusters;

    return (
      <div className={styles.clustersSection}>
        <h3 className={styles.clustersTitle}>Clusters</h3>
        <div className={styles.clustersCollapse} role="tablist">
          {displayClusters.map((cluster, index) => {
            const relevanceClass = getRelevanceClass(
              cluster.relevance_score,
              styles
            );
            const hasValidScore = isValidScore(cluster.relevance_score);
            const clusterKey = `cluster-${cluster.cluster_id}`;
            const isOpen = panelsOpen[index] || false;

            const headerContent = hasValidScore ? (
              <div className={styles.clusterHeader}>
                <span className={styles.clusterTitleText}>
                  {cluster.cluster_title}
                </span>
                <span
                  className={classnames(
                    styles.relevanceIndicator,
                    relevanceClass
                  )}
                  title={`Relevance: ${Math.round(
                    parseFloat(cluster.relevance_score) * 100
                  )}%`}
                />
              </div>
            ) : (
              cluster.cluster_title
            );

            return (
              <div key={clusterKey} className={classnames(styles.clusterPanel, relevanceClass)}>
                <ExpansionPanel>
                  <ExpansionPanelTitle
                    isOpen={isOpen}
                    onClick={() => handlePanelToggle(index)}
                  >
                    {headerContent}
                  </ExpansionPanelTitle>
                  <ExpansionPanelBody isOpen={isOpen}>
                    {renderClusterContent(cluster)}
                  </ExpansionPanelBody>
                </ExpansionPanel>
              </div>
            );
          })}
        </div>

        {hasRelevanceScores &&
          irrelevantClusters.length > 0 &&
          !showAllClusters && (
          <div className={styles.showMoreContainer}>
            <button
              type="button"
              onClick={() => setShowAllClusters(true)}
              className={styles.showMoreButton}
            >
              {relevantClusters.length === 0
                ? `Only ${irrelevantClusters.length} low-relevance ` +
                  `cluster${irrelevantClusters.length !== 1 ? 's' : ''} found. Show?`
                : `Show ${irrelevantClusters.length} less relevant ` +
                  `cluster${irrelevantClusters.length !== 1 ? 's' : ''}`}
            </button>
          </div>
        )}

        {hasRelevanceScores &&
          showAllClusters &&
          irrelevantClusters.length > 0 && (
          <div className={styles.showMoreContainer}>
            <button
              type="button"
              onClick={() => setShowAllClusters(false)}
              className={styles.showLessButton}
            >
              Hide less relevant clusters
            </button>
          </div>
        )}
      </div>
    );
  };

  if (!featureEnabled) {
    return null;
  }

  const shouldShowPanel = hasContent || isLoading || showIntentionFlow;
  const isCommandCenter = document.body.classList.contains('command-center');

  if (jsonContent) {
    try {
      const rawContent =
        typeof jsonContent === 'string' ? JSON.parse(jsonContent) : jsonContent;

      parsedContent = {
        summary: rawContent.overall_summary,
        events: rawContent.clusters || []
      };
    } catch (e) {
      console.error('Error parsing JSON content:', e);
    }
  }

  return (
    <ThemeProvider theme={Light}>
      <div
        className={classnames(styles.dashboardSummary, {
          [styles.isCommandCenter]: isCommandCenter
        })}
      >
        {isLoading && <div className={styles.loadingBar} />}

        {shouldShowPanel && (
          <div
            ref={summaryPanelRef}
            className={classnames(styles.summaryPanel, {
              [styles.expanded]: isExpanded
            })}
          >
            <div
              className={styles.summaryHeader}
              onClick={handleToggle}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleToggle();
                }
              }}
              tabIndex={0}
              role="button"
              aria-expanded={isExpanded}
              aria-controls="dashboard-summary-content"
            >
              <div className={styles.summaryTitle}>
                {getStatusIndicator()}
                Summary
                {cacheInfo && hasContent && (
                  <div className={styles.cacheStatus}>
                    <ClockCircleOutlined className={styles.cacheIcon} />
                    <span className={styles.cacheText}>{getCacheStatusDisplay()}</span>
                    <ToolTip
                      tooltipText={cacheInfo.fromCache
                        ? 'Refresh summary with latest data'
                        : 'Data is already fresh'}
                    >
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRefreshSummary();
                        }}
                        disabled={!cacheInfo.fromCache || isLoading}
                        className={styles.refreshButton}
                      >
                        <ReloadOutlined />
                      </button>
                    </ToolTip>
                  </div>
                )}
              </div>
              <span
                className={classnames(styles.toggleIcon, {
                  [styles.expanded]: isExpanded
                })}
              >
                ▼
              </span>
            </div>

            <div id="dashboard-summary-content" className={styles.summaryContent}>
              {showIntentionFlow ? (
                <div
                  className={classnames(styles.intentionFlowContainer, {
                    [styles.isCommandCenter]: isCommandCenter
                  })}
                >
                  <IntentionFlow
                    onSave={handleIntentionSave}
                    onCancel={handleIntentionCancel}
                    existingIntention={dashboardIntention || ''}
                    isEditing={isEditingIntention}
                    dashboardId={dashboardId}
                    activeProfileId={activeProfileId}
                    dashboardWidgets={dashboardWidgets}
                  />
                </div>
              ) : (
                hasContent &&
                parsedContent && (
                  <div>
                    <div className={styles.overallSummary}>
                      <h3 className={styles.overallTitle}>Overall Summary</h3>
                      <p>{parsedContent.summary}</p>
                    </div>
                    {parsedContent.events &&
                      parsedContent.events.length > 0 &&
                      renderClustersSection()}
                  </div>
                )
              )}
            </div>
          </div>
        )}
      </div>
    </ThemeProvider>
  );
};

DashboardSummary.propTypes = {
  dashboardId: PropTypes.string.isRequired,
  account: PropTypes.shape({
    summaryFeatureEnabled: PropTypes.bool
  }),
  activeFilter: PropTypes.object,
  activeProfileId: PropTypes.string,
  // Redux props
  dispatch: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  jsonContent: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  isExpanded: PropTypes.bool.isRequired,
  hasContent: PropTypes.bool.isRequired,
  featureEnabled: PropTypes.bool.isRequired,
  error: PropTypes.string,
  showIntentionFlow: PropTypes.bool.isRequired,
  dashboardIntention: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  dashboardIntentionPreset: PropTypes.string,
  isEditingIntention: PropTypes.bool.isRequired,
  cacheInfo: PropTypes.shape({
    fromCache: PropTypes.bool,
    cachedAt: PropTypes.string,
    ttlRemaining: PropTypes.number,
    cacheKey: PropTypes.string
  }),
  generatedAt: PropTypes.string
};

const mapStateToProps = state => ({
  isLoading: getIsLoading(state),
  jsonContent: getJsonContent(state),
  isExpanded: getIsExpanded(state),
  hasContent: getHasContent(state),
  featureEnabled: getFeatureEnabled(state),
  error: getError(state),
  showIntentionFlow: getShowIntentionFlow(state),
  dashboardIntention: getDashboardIntention(state),
  dashboardIntentionPreset: getDashboardIntentionPreset(state),
  isEditingIntention: getIsEditingIntention(state),
  cacheInfo: getCacheInfo(state),
  generatedAt: getGeneratedAt(state)
});

export { DashboardSummary };

export default connect(mapStateToProps)(DashboardSummary);
